import React from "react";
import Slider from "react-slick";
import { TiStarburst } from "react-icons/ti";
import { FaCheckCircle } from "react-icons/fa";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { getCoursePricing } from "../../utils/coursePricing";

const getDisplayedText = (desc, isExpanded, charLimit) => {
  if (isExpanded || !desc) return desc;
  return desc.length > charLimit ? desc.substring(0, charLimit) + " ..." : desc;
};

const getButtonStyle = (isSelected, submitted) => ({
  backgroundColor: isSelected ? "#038d03f1" : submitted ? "#ccc" : "#081339e1",
  cursor: submitted && !isSelected ? "not-allowed" : "pointer",
});

const SKELETON_COUNT = 2;

const CourseCardSkeleton = () => (
  <div className="card course-card-skeleton" aria-hidden="true">
    <div className="skeleton-block skeleton-image" />
    <div className="down-content">
      <div className="skeleton-block skeleton-title" />
      <div className="skeleton-block skeleton-line" />
      <div className="skeleton-block skeleton-line skeleton-line-short" />
      <div className="skeleton-block skeleton-line skeleton-line-shorter" />
    </div>
    <div className="row">
      <span className="col-12 price-yes">
        <div className="skeleton-price-row">
          <div className="skeleton-block skeleton-price" />
          <div className="skeleton-block skeleton-price skeleton-price-final" />
        </div>
      </span>
    </div>
    <div className="skeleton-block skeleton-button" />
  </div>
);

const CourseCard = ({
  courses,
  loading = false,
  isExpanded,
  toolTip,
  isVisible,
  sub,
  submitted,
  setToolTip,
  handleToolTip,
  handleCoursesChange,
  charLimit,
  selectedCountry,
}) => {
  const settings = {
    infinite: courses?.length > 2,
    swipe: courses?.length > 2,
    arrows: courses?.length > 2,
    slidesToShow: 2,
    slidesToScroll: 1,
    responsive: [
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          infinite: courses?.length > 1,
          swipe: courses?.length > 1,
          arrows: courses?.length > 1,
        },
      },
    ],
  };

  const skeletonSettings = {
    infinite: false,
    swipe: false,
    arrows: false,
    slidesToShow: 2,
    slidesToScroll: 1,
    responsive: [
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          infinite: false,
          swipe: false,
          arrows: false,
        },
      },
    ],
  };

  if (loading) {
    return (
      <Slider key="course-skeleton" {...skeletonSettings}>
        {Array.from({ length: SKELETON_COUNT }).map((_, index) => (
          <div key={`course-skeleton-${index}`} className="card-item">
            <CourseCardSkeleton />
          </div>
        ))}
      </Slider>
    );
  }

  return (
    <Slider key={`course-list-${courses?.length || 0}`} {...settings}>
      {courses?.map((item) => {
        const { qcId, description, logo, questionCategory } = item;

        const pricing = getCoursePricing(item, selectedCountry);
        const {
          isDiscounted,
          originalPrice,
          finalPrice,
          discountValue,
          discountUnit,
          currencySymbol,
        } = pricing;

        const isTruncated = description?.length > charLimit && !isExpanded;
        const displayedText = getDisplayedText(
          description,
          isExpanded,
          charLimit,
        );
        const showToolTip = toolTip === qcId;
        const isSelected = sub.includes(qcId);
        const buttonStyle = getButtonStyle(isSelected, submitted);

        return (
          <div key={qcId} className="card-item">
            <div
              className="card"
              style={{ position: "relative", overflow: "hidden" }}
            >
              <img
                src={logo}
                alt={questionCategory}
                style={{ width: "100%" }}
              />

              {isDiscounted && (
                <div
                  className="row"
                  style={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    zIndex: 1,
                    padding: "10px",
                  }}
                >
                  <span className="col-12" style={{ position: "relative" }}>
                    <div
                      className="starburst-container"
                      style={{ position: "relative" }}
                    >
                      <div className="starburst">
                        <TiStarburst className="starburst-icon" />
                      </div>
                      <p
                        style={{
                          position: "absolute",
                          top: "15%",
                          left: "61%",
                          transform: "translate(-20%, -20%)",
                          zIndex: 2,
                          textAlign: "center",
                        }}
                      >
                        {discountUnit === "P" && (
                          <span className="offer">
                            {` ${discountValue}%`}
                            <br /> Off
                          </span>
                        )}

                        {discountUnit === "F" && (
                          <span className="offer">
                            {`${currencySymbol}${discountValue}`}
                            <br /> Off
                          </span>
                        )}
                      </p>
                    </div>
                  </span>
                </div>
              )}

              <div className="down-content">
                {showToolTip && (
                  <div>
                    <p
                      className={`tooltip-text ${isVisible ? "show" : ""}`}
                      style={{ position: "absolute" }}
                    >
                      {description}
                    </p>
                  </div>
                )}

                <h4>{questionCategory}</h4>
                <p style={{ overflow: "hidden" }}>
                  {displayedText}
                  {!isExpanded && isTruncated && (
                    <>
                      <br />
                      <button
                        onMouseEnter={() => handleToolTip(qcId)}
                        onMouseLeave={() => setToolTip(null)}
                        className="Read_More"
                      >
                        Read more
                      </button>
                    </>
                  )}
                </p>
              </div>

              <div className="row">
                <span
                  className={`col-12 ${
                    isDiscounted ? "price-yes" : "price-no"
                  }`}
                >
                  <p>
                    {isDiscounted && (
                      <span className="muted">
                        {currencySymbol}
                        {Number(originalPrice).toFixed(2)}
                      </span>
                    )}{" "}
                    <span className="finalP">
                      {currencySymbol}
                      {Number(
                        isDiscounted ? finalPrice : originalPrice,
                      ).toFixed(2)}
                    </span>
                  </p>
                </span>
              </div>

              <button
                type="button"
                className={`courses-button ${isSelected ? "selected" : ""}`}
                name="subject"
                onClick={() => handleCoursesChange(item)}
                disabled={submitted && !isSelected}
                style={buttonStyle}
              >
                {isSelected && <FaCheckCircle />}
                {isSelected ? "Selected" : "Select"}
              </button>
            </div>
          </div>
        );
      })}
    </Slider>
  );
};

export default CourseCard;
